'use client';

import { useState } from 'react';

export interface ShareLinksProps {
  /** Canonical URL of the page being shared. */
  url: string;
  title: string;
  className?: string;
}

const ICON_CLASS =
  'flex size-9 items-center justify-center rounded-full border border-brand-navy/15 text-brand-navy transition-colors hover:border-brand-royal hover:text-brand-royal';

/** Social share row — X, Facebook, LinkedIn, email, copy link. */
export function ShareLinks({ url, title, className = '' }: ShareLinksProps) {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the links still work */
    }
  };

  const networks = [
    {
      label: 'Share on X',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      path: 'M14.2 10.16 22.03 1h-1.85l-6.8 7.95L7.94 1H1.67l8.21 12.02L1.67 22.6h1.85l7.18-8.4 5.74 8.4h6.27L14.2 10.16Zm-2.54 2.97-.83-1.2L4.2 2.4h2.85l5.34 7.69.83 1.2 6.94 10h-2.85l-5.66-8.15Z',
    },
    {
      label: 'Share on Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      path: 'M13.5 21.9v-8.4h2.82l.42-3.27H13.5V8.14c0-.95.26-1.59 1.62-1.59h1.73V3.62c-.3-.04-1.33-.13-2.53-.13-2.5 0-4.21 1.53-4.21 4.33v2.42H7.28v3.27h2.83v8.4h3.39Z',
    },
    {
      label: 'Share on LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      path: 'M6.94 8.75H3.56V21.5h3.38V8.75ZM5.25 2.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM21.5 14.13c0-3.44-1.84-5.63-4.6-5.63-1.5 0-2.6.7-3.15 1.7h-.07l-.13-1.45H10.5c.04 1 .07 2.13.07 3.44v9.31h3.38v-7.13c0-.34.03-.68.13-.92.27-.68.88-1.38 1.9-1.38 1.33 0 2.14 1.03 2.14 2.53v6.9h3.38v-7.37Z',
    },
    {
      label: 'Share by email',
      href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      path: 'M3 5.5h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1Zm9 6.8 8.5-5.3H3.5l8.5 5.3Zm0 2.2L3.5 9.2v8.3h17V9.2L12 14.5Z',
    },
  ] as const;

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <span className="mr-1 text-xs font-bold tracking-widest text-brand-navy/70 uppercase">
        Share
      </span>
      {networks.map((network) => (
        <a
          key={network.label}
          href={network.href}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={network.label}
          className={ICON_CLASS}
        >
          <svg aria-hidden viewBox="0 0 24 24" className="size-4" fill="currentColor">
            <path d={network.path} />
          </svg>
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'Link copied' : 'Copy link'}
        className={`${ICON_CLASS} relative`}
      >
        {copied ? (
          <svg aria-hidden viewBox="0 0 24 24" className="size-4 text-brand-royal" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg aria-hidden viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.07 0l3.18-3.18a5 5 0 0 0-7.07-7.07l-1.6 1.59" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 11a5 5 0 0 0-7.07 0l-3.18 3.18a5 5 0 0 0 7.07 7.07l1.59-1.59" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
}
